import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import XLSX from 'xlsx';
import { Field } from 'redux-form/immutable';
import { List, Map } from 'immutable';

// mui components
import { lighten } from '@material-ui/core/styles/colorManipulator';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Icon from '@material-ui/core/Icon';
import Toolbar from '@material-ui/core/Toolbar';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

// local components
import SelectInput from '../../../../../SelectInput';
import Button from '../../../../../Button';
import ActionModal from '../../../../../ActionModal';

function TableToolbar({
  classes,
  currentChapterId,
  deleteMatch,
  icon,
  isCdSite,
  matches,
  numEvents,
  rounds,
  selectedRound,
  subtitle,
  title,
  users,
  warningMessage,
}) {
  TableToolbar.propTypes = {
    classes:          PropTypes.instanceOf(Object).isRequired,
    currentChapterId: PropTypes.number.isRequired,
    deleteMatch:      PropTypes.func.isRequired,
    icon:             PropTypes.string.isRequired,
    isCdSite:         PropTypes.bool.isRequired,
    matches:          PropTypes.instanceOf(List).isRequired,
    numEvents:        PropTypes.number.isRequired,
    rounds:           PropTypes.instanceOf(List).isRequired,
    selectedRound:    PropTypes.instanceOf(Map).isRequired,
    subtitle:         PropTypes.string.isRequired,
    title:            PropTypes.string.isRequired,
    users:            PropTypes.instanceOf(List).isRequired,
    warningMessage:   PropTypes.string,
  };

  TableToolbar.defaultProps = {
    warningMessage: '',
  };

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const idAttribute = isCdSite ? 'group_pnm_id' : 'leg_tech_pnm_id';

  const getRoundOptions = () => {
    const options = [];

    rounds.forEach((round) => {
      options.push({
        label: round.get('name'),
        value: round.get('_id'),
      });
    });

    return options;
  };

  const getMatchesForUser = user => matches.filter(m =>
    m.getIn(['user', 'id']) === user.get('id'));

  const formatData = (items) => {
    // Creates an array of arrays without keys, only properties for exporting
    const data = [];

    items.forEach((item) => {
      const userMatches = getMatchesForUser(item);

      const rowData = [
        item.get('firstname'),
        item.get('lastname'),
        item.get('matchRecs'),
        item.get('officialMatches'),
        selectedRound.get('name'),
      ];

      // Loop events
      for (let index = 0; index < numEvents; index += 1) {
        const eventNumber = index + 1;
        const matchesForEvent = userMatches.filter(m => m.getIn(['pnm', 'eventForRound'], 0) === eventNumber);
        let firstNameCell = '';
        let lastNameCell = '';
        let councilIdCell = '';
        const pnmIdsUsed = [];

        // Loop PNM matches
        if (matchesForEvent.size) {
          matchesForEvent.forEach((m, matchIndex) => {
            const firstName = m.getIn(['pnm', 'firstname'], '');
            let lastName = m.getIn(['pnm', 'lastname'], '');
            const councilId = m.getIn(['pnm', idAttribute]);

            // Don't put duplicate matches on export
            if (pnmIdsUsed.includes(m.getIn(['pnm', 'id']))) return;
            pnmIdsUsed.push(m.getIn(['pnm', 'id']));

            if (m.get('matchStrength')) {
              lastName += ` (Match Strength ${m.get('matchStrength')})`;
            }

            // Add pnm to cell's match data
            if (matchIndex === 0) {
              firstNameCell = firstName;
              lastNameCell = lastName;
              councilIdCell = councilId;
            } else {
              firstNameCell += `, ${firstName}`;
              lastNameCell += `, ${lastName}`;
              councilIdCell += `, ${councilId}`;
            }
          });
        }

        // Push string of all pnm matches for the event
        rowData.push(firstNameCell);
        rowData.push(lastNameCell);
        rowData.push(councilIdCell);
      }

      // Push array of strings to main sheet arrray
      data.push(rowData);
    });

    return data;
  };

  const getExportCols = () => {
    const newCols = [
      { key: 0, name: 'Member First Name' },
      { key: 1, name: 'Member Last Name' },
      { key: 2, name: 'Match Recommendations' },
      { key: 3, name: 'Official Matches' },
      { key: 4, name: 'Round' },
    ];

    for (let index = 0; index < numEvents; index += 1) {
      newCols.push({
        key:  newCols.length,
        name: `Event ${index + 1} PNM First Name`,
      });
      newCols.push({
        key:  newCols.length,
        name: `Event ${index + 1} PNM Last Name`,
      });
      newCols.push({
        key:  newCols.length,
        name: `Event ${index + 1} PNM ID`,
      });
    }

    return newCols;
  };

  const handleDeleteMatch = () => {
    const matchIds = matches.map(m => m.get('_id'));

    deleteMatch({
      chapter: currentChapterId,
      id:      null,
      ids:     matchIds.toJS(),
    });

    setConfirmModalOpen(false);
  };

  const exportFile = (cols, unformattedData, sheetName, fileName) => {
    const data = formatData(unformattedData);
    // make array of column names
    const colNames = [];
    cols.forEach(col =>
      colNames.push(col.name));

    // create array with cols and data combined
    data.unshift(colNames);

    // autofit table cell width
    const colWidths = [];
    colNames.forEach((item) => {
      // Sets the cell width in characters, multiplied for more spacing
      colWidths.push({ wch: item.length * 2.5 });
    });

    /* convert state to workbook */
    const ws = XLSX.utils.aoa_to_sheet(data);

    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    /* generate XLSX file and send to client */
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };

  const renderWarningMessage = () => {
    let element;

    if (warningMessage.length) {
      element = [
        <Grid item xs={ 1 } key='icon'>
          <Icon color='secondary'>error_outline</Icon>
        </Grid>,
        <Grid item xs={ 11 } align='left' key='label'>
          <Typography variant='body2' color='secondary'>{ warningMessage }</Typography>
        </Grid>,
      ];
    }

    return element;
  };

  const cols = getExportCols();
  const sheetName = 'Official Match Results Report';
  const fileName = 'official_match_results_report';

  return (
    <Toolbar className={ classes.root }>
      <Grid container className={ classes.tableToolbar }>
        <Grid item xs={ 4 } className={ classes.titleContainer }>
          <Icon className={ classes.titleIcon } color='primary'>{ icon }</Icon>
          <Typography variant='h5' color='primary'>{ title }</Typography>
        </Grid>

        <Hidden smDown>
          <Grid item xs={ 8 }>
            <Grid container alignItems='center' justify='flex-end' spacing={ 16 }>
              <Grid item xs={ 6 } sm={ 3 }>
                <Tooltip title={ `Delete all matches for ${selectedRound.get('name')}` }>
                  <Button color='secondary' variant='text' onClick={ () => setConfirmModalOpen(true) }>
                    <Icon className={ classes.leftIcon }>delete</Icon>

                    Delete All
                  </Button>
                </Tooltip>
              </Grid>

              <Grid item xs={ 6 } sm={ 3 }>
                <Tooltip title='Download results to spreadsheet'>
                  <Button color='primary' variant='contained' onClick={ () => exportFile(cols, users, sheetName, fileName) }>
                    <Icon className={ classes.leftIcon }>file_download</Icon>

                    Download
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        </Hidden>

        <Grid item xs={ 12 } align='left' className={ classes.subtitleContainer }>
          <Typography variant='subtitle1' color='textSecondary'>{ subtitle }</Typography>
        </Grid>

        <Grid item xs={ 12 } sm={ 4 } align='left'>
          <Field name='round'
            options={ getRoundOptions() }
            component={ SelectInput }
            label='Select Round'
            fullWidth
            required />
        </Grid>
        <Grid item xs={ 12 } sm={ 8 } />

        { renderWarningMessage() }
      </Grid>

      <ActionModal open={ confirmModalOpen }
        title={ `Delete all matches for ${selectedRound.get('name', 'selected round')}?` }
        description='Only matches created by hand or auto-generated for this round will be deleted.
                    All official matches for other rounds
                    or match recommendations made by your members will be unaffected'
        onAccept={ handleDeleteMatch }
        onReject={ () => setConfirmModalOpen(false) }
        acceptLabel='Delete'
        rejectLabel='Cancel' />

    </Toolbar>
  );
}

const styles = theme => ({
  highlight:
    theme.palette.type === 'light'
      ? {
        color:           theme.palette.secondary.main,
        backgroundColor: lighten(theme.palette.secondary.light, 0.85),
      }
      : {
        color:           theme.palette.text.primary,
        backgroundColor: theme.palette.secondary.dark,
      },
  spacer: {
    flex: '1 1 100%',
  },

  actions: {
    color: theme.palette.text.secondary,
  },

  leftIcon: {
    marginRight: theme.spacing.unit,
  },

  tableToolbar: {
    padding: 24,
  },

  title: {
    flex: '0 0 auto',
  },

  titleIcon: {
    verticalAlign: 'middle',
    marginRight:   20,
    fontSize:      30,
  },

  titleContainer: {
    display:       'flex',
    flexDirection: 'row',
    marginBottom:  20,
  },

  subtitleContainer: {
    marginTop: -10,
  },

  logo: {
    width: 105,
  },
});

export default withStyles(styles)(TableToolbar);
